import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    getFieldErrors,
    getFormError,
    getSelectedPayee,
    getTransactionsIsPosting,
} from 'lib/selectors';
import { useMakePayment } from 'lib/hooks';
import {
    PAYMENT_MAKE_PAYMENT,
    PAYMENT_MAKE_PAYMENT_SUCCESS,
} from 'constants/shared/routes';
import { hideModal, showModal } from 'actions/modal';
import {
    CONFIRM_AND_SEND_MODAL,
    ERROR_MODAL,
    PAYMENT_FEE_MODAL,
} from 'constants/shared/modalTypes';
import { dispatchHideModal } from 'utils/modals';
import PageContainer from 'components/shared/generic/pageContainer/presentational/PageContainer';
import MakePaymentForm from '../presentational/MakePaymentForm';
import SectionTitle from 'components/shared/generic/sectionTitle/presentational/SectionTitle';
import { usePrevious } from 'lib/utils';
import {
    REACT_APP_SUITSME_SORTCODE_IDT,
    REACT_APP_SUITSME_SORTCODE_EPS,
} from 'config';
import { trackCustomerIOEvent } from 'utils/customerIO';
import { useGetFeesQuery } from 'lib/services/fees';
import { Payee, CustomerIOEvent } from 'lib/type';

const MakePaymentFormContainer = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const selectedPayee: Payee = useSelector(getSelectedPayee);
    const { data: allFees } = useGetFeesQuery();
    const { internalTransactionFee, externalTransactionFee } = allFees || {};
    const isInternalPayee =
        selectedPayee.sortCode === REACT_APP_SUITSME_SORTCODE_IDT ||
        selectedPayee.sortCode === REACT_APP_SUITSME_SORTCODE_EPS;
    const {
        form,
        handleChange,
        handleSubmit,
        isPosting,
        error,
    } = useMakePayment(onSubmit, onError);

    const fieldErrors = useSelector(getFieldErrors);
    const formError = useSelector(getFormError);

    const prevState = usePrevious({ fieldErrors, formError });

    const handleCloseModal = useCallback(() => {
        dispatch(hideModal());
    }, [dispatch]);

    useEffect(() => {
        const isNewFormError = formError && !prevState.formError;
        const isNewFieldErrors =
            Object.keys(fieldErrors as object)?.length >
            Object.keys(prevState.fieldErrors as object).length;

        if (isNewFormError || isNewFieldErrors) {
            handleCloseModal();
        }
    }, [fieldErrors, formError, prevState, handleCloseModal]);

    const handleConfirmModal = () => {
        const fee = selectedPayee.isSuitsMeCustomer
            ? internalTransactionFee
            : externalTransactionFee;
        if (!form.amount || form.amount === '£0.00') {
            dispatch(
                showModal({
                    type: ERROR_MODAL,
                    props: {
                        title: 'Invalid amount',
                        message: 'Please enter an amount larger than £0.00',
                        onClose: () => handleCloseModal(),
                    },
                }),
            );
        } else if (isInternalPayee || +fee === 0) {
            handleShowConfirmAndSendModal();
        } else {
            dispatch(
                showModal({
                    type: PAYMENT_FEE_MODAL,
                    props: {
                        fee,
                        additionalCost: form.Amount * 100,
                        handleSubmit: handleShowConfirmAndSendModal,
                        closeModal: handleCloseModal,
                        isInternalPayee,
                    },
                }),
            );
        }
    };

    const handleShowConfirmAndSendModal = () => {
        dispatch(
            showModal({
                type: CONFIRM_AND_SEND_MODAL,
                props: {
                    form,
                    selectedPayee,
                    handleSubmit,
                    error,
                    postingSelector: getTransactionsIsPosting,
                    type: 'make-payment',
                },
            }),
        );
    };

    function onSubmit() {
        dispatchHideModal(dispatch);
        history.push(PAYMENT_MAKE_PAYMENT_SUCCESS, {
            data: {
                amount: form.amount,
                recipient: selectedPayee.payeeName,
            },
        });
        const formattedAmount = Number.parseInt((form.amount * 100).toFixed(0));
        trackCustomerIOEvent(CustomerIOEvent.MAKE_PAYMENT, {
            payeeName: selectedPayee.payeeName,
            amount: -formattedAmount,
        });
    }

    function onError() {
        dispatchHideModal(dispatch);
        history.push(PAYMENT_MAKE_PAYMENT_SUCCESS, {
            data: {
                error,
            },
        });
    }

    useEffect(() => {
        if (!selectedPayee) {
            history.push(PAYMENT_MAKE_PAYMENT);
        }
    }, [history, selectedPayee]);

    return (
        <PageContainer className="make-payment-form-container">
            <SectionTitle
                title={selectedPayee?.payeeName}
                backRoute={PAYMENT_MAKE_PAYMENT}
                additionalText={`${selectedPayee?.accountNo} | ${selectedPayee?.sortCode}`}
            />
            <MakePaymentForm
                {...form}
                handleChange={handleChange}
                handleSubmit={handleConfirmModal}
                isPosting={isPosting}
            />
        </PageContainer>
    );
};

export default MakePaymentFormContainer;
